import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';

import { reportError } from '../../utils/reportError.ts';
import type { Snippet } from '../../types/snippet.ts';

type EditorAPIWithPreservedSelection = PublicPluginAPI<[QuickInsertPlugin]> & {
	blockControls?: {
		commands?: {
			stopPreservingSelection?: () => ({ tr }: { tr: unknown }) => unknown;
		};
		sharedState?: {
			currentState?: () => { preservedSelection?: Selection } | undefined;
		};
	};
};

const getInsertPositionAfterSelection = (selection?: Selection): number | undefined => {
	if (!selection) {
		return undefined;
	}

	const blockRange = selection.$from.blockRange(selection.$to);
	return blockRange?.end ?? selection.to;
};

const getPreservedSelection = (
	editorAPI: EditorAPIWithPreservedSelection,
	shouldUsePreservedSelection?: boolean,
): Selection | undefined => {
	if (!shouldUsePreservedSelection) {
		return undefined;
	}

	return editorAPI.blockControls?.sharedState?.currentState?.()?.preservedSelection;
};

/**
 * Hook for inserting a snippet at the current cursor position in the editor.
 * Pass editorAPI from the caller (e.g. useFullPageEditorApiState or EditorContextPanelContext).
 *
 * @returns A function to insert a snippet, returns true on success, false on failure
 */
export const useInsertSnippet = (editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null) => {
	return (snippet: Snippet, insertBelowPreservedSelection = false): boolean => {
		if (!editorAPI?.core?.actions?.execute) {
			return false;
		}

		const body = snippet.body;
		if (!body) {
			return false;
		}

		const editorAPIWithPreservedSelection = editorAPI as EditorAPIWithPreservedSelection;
		const preservedSelection = getPreservedSelection(
			editorAPIWithPreservedSelection,
			insertBelowPreservedSelection,
		);

		try {
			editorAPI.core.actions.execute(({ tr }: { tr: any }) => {
				const schema = tr.doc.type.schema;

				// Convert snippet body to ProseMirror content
				// Body can be either a single ADFEntity or an array of ADFEntity
				const content = Array.isArray(body)
					? Fragment.fromJSON(schema, body)
					: schema.nodeFromJSON(body);

				const insertPosition = getInsertPositionAfterSelection(preservedSelection);
				const nextTr = safeInsert(content, insertPosition, insertPosition === undefined)(tr) ?? tr;

				if (preservedSelection) {
					editorAPIWithPreservedSelection.blockControls?.commands?.stopPreservingSelection?.()({
						tr: nextTr,
					});
				}

				return nextTr;
			});

			return true;
		} catch (error) {
			reportError(error);
			return false;
		}
	};
};
